import type { ReactNode } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/ui/loader";

/**
 * Shared card wrapper for every OTC report.
 *
 * Each report card owns its own filter state and supplies:
 *   - title / description
 *   - the filter inputs (children)
 *   - whether generate is disabled
 *   - the download handler
 *   - the rendered result links (linkContent), which the caller is
 *     responsible for clearing whenever a new generate run starts.
 */
export function ReportCard({
  title,
  description,
  isDownloading,
  isGenerateDisabled,
  onGenerate,
  hasResults,
  linkContent,
  children,
}: {
  title: string;
  description?: string;
  isDownloading: boolean;
  isGenerateDisabled?: boolean;
  onGenerate: () => void;
  hasResults: boolean;
  linkContent?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        {description ? (
          <CardDescription>{description}</CardDescription>
        ) : null}
      </CardHeader>
      {children ? (
        <CardContent className="flex flex-col gap-4">{children}</CardContent>
      ) : null}
      <CardFooter className="mt-auto flex flex-col items-stretch gap-3">
        <div className="flex flex-row items-center justify-between">
          {isDownloading ? (
            <Loader />
          ) : (
            <Button
              variant="outline"
              type="button"
              size="sm"
              onClick={onGenerate}
              disabled={!!isGenerateDisabled}
            >
              Generate
            </Button>
          )}
          {hasResults && !isDownloading ? (
            <div className="flex flex-col items-end gap-1 text-sm">
              {linkContent}
            </div>
          ) : null}
        </div>
      </CardFooter>
    </Card>
  );
}

export function ReportDownloadLink({
  href,
  label,
}: {
  href: string;
  label: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="underline underline-offset-1 decoration-sky-500"
    >
      {label}
    </a>
  );
}
